/*
 * The service page (prototype: .vFlow crumbs, .o1Grid, .o1Title, .facts, .o1Sec, .dl; booking-web.html Option 1).
 * The event page draws the same layout from this module. Colors are theme tokens only.
 */
@layer telify {
  .page {
    padding-bottom: 72px;
  }

  .crumbs {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    padding: 26px 0 0;
    font-size: 13.5px;
    color: var(--text-tertiary);
  }

  .sep {
    color: var(--text-muted);
  }

  .here {
    color: var(--text-secondary);
  }

  .grid {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 392px;
    gap: 64px;
    margin-top: 22px;
    align-items: start;
  }

  /* As tall as the row, so the booking column inside can stay in view while the page scrolls. */
  .side {
    align-self: stretch;
  }

  .photos:empty {
    display: none;
  }

  .title {
    margin-top: 40px;
  }

  .photos:empty + .title {
    margin-top: 0;
  }

  .h1 {
    font-family: var(--font-display);
    font-weight: 500;
    font-size: 58px;
    letter-spacing: -0.015em;
    line-height: 1.05;
    overflow-wrap: anywhere;
  }

  .lead {
    margin-top: 14px;
    max-width: 38ch;
    font-size: 19px;
    line-height: 1.55;
    color: var(--text-secondary);
  }

  .facts {
    display: flex;
    margin-top: 30px;
    border-top: var(--line);
    border-bottom: var(--line);
  }

  .fact {
    flex: 1;
    min-width: 0;
    padding: 16px 20px;
    border-left: var(--line);
  }

  .fact:first-child {
    border-left: 0;
    padding-left: 0;
  }

  .factK {
    font-size: 12.5px;
    color: var(--text-tertiary);
  }

  .factV {
    margin-top: 4px;
    font-size: 17px;
    font-weight: 500;
  }

  .accent {
    color: var(--accent);
  }

  .low {
    color: var(--spots-low-color);
  }

  .sec {
    padding: 44px 0 0;
  }

  .h2 {
    margin-bottom: 16px;
    font-family: var(--font-display);
    font-weight: 500;
    font-size: 28px;
    letter-spacing: -0.01em;
    line-height: 1.15;
  }

  .body p {
    max-width: 64ch;
    font-size: 16.5px;
    line-height: 1.75;
    color: var(--text-secondary);
    white-space: pre-line;
    overflow-wrap: anywhere;
  }

  .body p + p {
    margin-top: 14px;
  }

  /* Location and cancellation: a ruled list with no header over it */
  .dl {
    display: grid;
    grid-template-columns: 180px minmax(0, 1fr);
    margin-top: 52px;
    border-top: var(--line);
  }

  .dl > div {
    display: contents;
  }

  .dl dt,
  .dl dd {
    padding: 16px 0;
    border-bottom: var(--line);
    font-size: 15.5px;
  }

  .dl dt {
    color: var(--text-tertiary);
  }

  .dl dd small {
    display: block;
    margin-top: 2px;
    font-size: 14px;
    color: var(--text-tertiary);
  }

  /* Message the studio right under the details: their last line is its rule (bookings.html .dl + .o1Chat). */
  .message > [data-telify-root] {
    padding-top: 40px;
    border-top: 0;
  }

  .error {
    padding: 64px 0;
    font-size: 17px;
    color: var(--text-secondary);
  }

  .crumbs a {
    transition: color 0.15s ease;
  }

  @media (hover: hover) {
    .crumbs a:hover {
      color: var(--text-primary);
      text-decoration: underline;
      text-underline-offset: 3px;
    }
  }

  @media (max-width: 1023px) {
    .grid {
      grid-template-columns: minmax(0, 1fr);
      gap: 40px;
    }

    .h1 {
      font-size: 40px;
    }

    /* Two facts to a line, so a long date never pushes the page sideways */
    .facts {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .fact:nth-child(odd) {
      border-left: 0;
      padding-left: 0;
    }

    .fact:nth-child(n + 3) {
      border-top: var(--line);
    }

    .dl {
      grid-template-columns: 120px minmax(0, 1fr);
    }
  }

  /* web-studio-sites D12: on a custom site the page is the website's drawing at every width, folded as Dana's
     confirmed bookings.html folds it (.o1Grid at 1024 pixels, .facts at 860; its title and details already match the
     rules above). The standard website keeps the rules above. */
  @media (max-width: 1024px) {
    :global(:root[data-custom-site]) .grid {
      grid-template-columns: minmax(0, 1fr);
      gap: 28px;
    }
  }

  @media (max-width: 860px) {
    /* The facts in one row across when they fit (a service's length, format and price), wrapping only a long one
       (card 4 walk, Gabriel 2026-11-01: "this should be kit wide") */
    :global(:root[data-custom-site]) .facts {
      display: flex;
      flex-wrap: wrap;
      column-gap: 22px;
    }

    :global(:root[data-custom-site]) .fact {
      flex: 1 1 auto;
      padding: 14px 0;
      border-left: 0;
      border-top: 0;
    }

    :global(:root[data-custom-site]) .fact > :last-child {
      white-space: nowrap;
    }
  }
}
